import React, { useState } from 'react';
import { Spin, Tooltip, Modal, Table, Tag, Typography, Input, Button, message } from 'antd';
import { 
  UserOutlined, 
  BankOutlined, 
  BarChartOutlined, 
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SearchOutlined,
  ClearOutlined,
  DownloadOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useStaffMetrics, useSiteMetrics, useIncidentTypeMetrics, useLastHumanActivity } from '../hooks/useDashboardMetrics';
import { useDetailedStaffData, useDetailedSiteData, useDetailedIncidentData } from '../hooks/useDetailedDashboardData';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { useMultiCmt } from '../contexts/MultiCmtContext';
import { exportDashboardSiteStatus, exportDashboardStaffStatus } from '../utils/dashboardExportUtils';
import { StatusMetricGroup } from './StatusMetricGroup';
import { StatusMetricCard } from './StatusMetricCard';
import CurrentIncidentsContainer from './CurrentIncidentsContainer';
import LastUpdateContainer from './LastUpdateContainer';

const { Text, Title } = Typography;

// 📱 Responsive breakpoint detection hook
const useResponsiveBreakpoint = () => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  React.useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const getBreakpoint = () => {
    if (screenWidth < 768) return 'mobile';
    if (screenWidth < 1200) return 'tablet';
    if (screenWidth < 1600) return 'desktop';
    return 'large';
  };
  
  return {
    screenWidth,
    breakpoint: getBreakpoint(),
    isMobile: screenWidth < 768,
    isTablet: screenWidth >= 768 && screenWidth < 1200,
    isDesktop: screenWidth >= 1200 && screenWidth < 1600,
    isLarge: screenWidth >= 1600
  };
};

/**
 * Enterprise-Level Top Strip Metrics Bar
 * Displays high-level situational awareness across multiple concurrent crises
 */
const EnterpriseMetricsBar = ({ orgId: propOrgId }) => {
  console.log('🚩 CANARY: EnterpriseMetricsBar component mounted at top');
   
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const { activeCmt } = useMultiCmt();
  
  // FIXED: Guarantee non-null orgId resolution
  const orgId = propOrgId || currentUser?.orgId || currentUser?.organizationId;
  
  console.log('🚩 ORGANIZATION ID DEBUG:', {
    propOrgId,
    userOrgId: currentUser?.orgId,
    userOrganizationId: currentUser?.organizationId,
    finalOrgId: orgId,
    user: currentUser?.email
  });

  const staffMetrics = useStaffMetrics(orgId);
  const siteMetrics = useSiteMetrics(orgId);
  const incidentTypes = useIncidentTypeMetrics(orgId);
  const lastActivity = useLastHumanActivity(orgId);

  // DEBUG: Log what we're getting from the hook
  console.log('🚨 ENTERPRISE METRICS DEBUG:', {
    orgId,
    staffMetrics,
    siteMetrics,
    timestamp: new Date().toISOString()
  });

  // Modal state management
  const [showStaffModal, setShowStaffModal] = useState(false);
  
  // Export state management
  const [staffExportLoading, setStaffExportLoading] = useState(false);
  const [siteExportLoading, setSiteExportLoading] = useState(false);
  
  // Badge modal state - unified for both staff and site badges
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [selectedBadgeFilter, setSelectedBadgeFilter] = useState('');
  const [badgeModalType, setBadgeModalType] = useState(''); // 'staff' or 'site'
  
  // Site detail modal state
  const [showSiteDetailModal, setShowSiteDetailModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  
  // Responsive breakpoint detection
  const responsive = useResponsiveBreakpoint();
  
  // Enhanced data hooks for modals
  const detailedStaffData = useDetailedStaffData(orgId, selectedBadgeFilter, showBadgeModal && badgeModalType === 'staff');
  const detailedSiteData = useDetailedSiteData(orgId, selectedBadgeFilter, showBadgeModal && badgeModalType === 'site');
  const detailedIncidentData = useDetailedIncidentData(orgId);

  // Add enterprise styling
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .enterprise-badge {
        background: linear-gradient(135deg, #1890ff, #722ed1);
        color: white;
        border: none;
        border-radius: 8px;
        padding: 12px 16px;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 80px;
        flex: 1;
        margin: 0 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      }
      
      .enterprise-badge:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(0,0,0,0.25);
      }
      
      .enterprise-badge.green {
        background: linear-gradient(135deg, #52c41a, #389e0d);
      }
      
      .enterprise-badge.orange {
        background: linear-gradient(135deg, #fa8c16, #d46b08);
      }
      
      .enterprise-badge.red {
        background: linear-gradient(135deg, #ff4d4f, #cf1322);
      }
      
      .enterprise-badge.blue {
        background: linear-gradient(135deg, #1890ff, #096dd9);
      }
      
      .enterprise-badge.purple {
        background: linear-gradient(135deg, #722ed1, #531dab);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Format time ago for last activity with full text dates
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'No recent activity';
    
    const now = new Date();
    const activityTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    
    // Use full text date format
    return activityTime.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Format last activity details
  const formatLastActivity = (activity) => {
    if (!activity) return 'No recent activity';
    
    const userName = activity.userFullName || activity.userName || 'Unknown User';
    const action = activity.details || activity.action || 'performed an action';
    const timeAgo = formatTimeAgo(activity.timestamp);
    
    // Shorten long descriptions
    const shortAction = action.length > 50 ? action.substring(0, 50) + '...' : action;
    
    return `${userName}: ${shortAction} @ ${timeAgo}`;
  };

  // Badge click handlers
  const handleBadgeClick = (e, badgeType, filterValue, modalType) => {
    e.stopPropagation(); // Prevent card click
    setSelectedBadgeFilter(filterValue);
    setBadgeModalType(modalType);
    setShowBadgeModal(true);
  };

  const handleSiteClick = (site) => {
    setSelectedSite(site);
    setShowSiteDetailModal(true);
  };

  // Handle total sites count click - shows all sites with search
  const handleTotalSitesClick = (e) => {
    e.stopPropagation(); // Prevent card click
    setSelectedBadgeFilter('all'); // Special filter for all sites
    setBadgeModalType('site');
    setSiteSearchTerm(''); // Reset search
    setShowBadgeModal(true);
  };

  // Handle staff export
  const handleStaffExport = async (e) => {
    e.stopPropagation(); // Prevent modal from opening
    try {
      setStaffExportLoading(true);
      const filename = await exportDashboardStaffStatus(
        db,
        orgId,
        currentUser?.organization?.name || 'Organization'
      );
      message.success(`Staff status exported successfully as ${filename}`);
    } catch (error) {
      console.error('Error exporting staff status:', error);
      message.error('Failed to export staff status');
    } finally {
      setStaffExportLoading(false);
    }
  };

  // Handle site export
  const handleSiteExport = async (e) => {
    e.stopPropagation(); // Prevent modal from opening
    try {
      setSiteExportLoading(true);
      const filename = await exportDashboardSiteStatus(
        orgId,
        currentUser?.organization?.name || 'Organization'
      );
      message.success(`Site status exported successfully as ${filename}`);
    } catch (error) {
      console.error('Error exporting site status:', error);
      message.error('Failed to export site status');
    } finally {
      setSiteExportLoading(false);
    }
  };

  // Clear site search
  const clearSiteSearch = () => {
    setSiteSearchTerm('');
  };

  // FIXED: Early return with error display if no orgId
  if (!orgId) {
  return (
      <div style={{padding: 16, backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, marginBottom: 16}}>
        <div style={{ color: '#cf1322', fontWeight: 'bold', marginBottom: 8}}>⚠️ METRICS UNAVAILABLE</div>
        <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
          No orgId available - propOrgId: {JSON.stringify(propOrgId)}, user.orgId: {JSON.stringify(currentUser?.orgId)}
        </div>
      </div>
    );
  }

  if (staffMetrics.loading || siteMetrics.loading) {
    return (
      <div style={{padding: 16, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, marginBottom: 16}}>
        <div style={{ color: '#52c41a', fontWeight: 'bold' }}>📊 LOADING METRICS...</div>
        <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
          orgId: {orgId}, hooks loading...
        </div>
      </div>
    );
  }

  if (staffMetrics.error || siteMetrics.error) {
    return (
      <div style={{padding: 16, backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, marginBottom: 16}}>
        <div style={{ color: '#cf1322', fontWeight: 'bold', marginBottom: 8}}>❌ METRICS ERROR</div>
        <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
          Staff Error: {staffMetrics.error}<br/>
          Site Error: {siteMetrics.error}
        </div>
      </div>
    );
  }

  // Define staff status cards with exact ordering from briefing
  const staffStatusCards = [
    {
      label: 'Safe',
      count: staffMetrics.safe || 0,
      activeColorClass: 'bg-green-600 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'staff-safe',
      filterValue: 'safe'
    },
    {
      label: 'Help',
      count: staffMetrics.need_assistance || 0,
      activeColorClass: 'bg-amber-500 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'staff-help',
      filterValue: 'need_assistance'
    },
    {
      label: 'Unaccounted For',
      count: staffMetrics.unaccounted || 0,
      activeColorClass: 'bg-orange-500 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'staff-unaccounted',
      filterValue: 'unaccounted'
    },
    {
      label: 'Hospitalised',
      count: staffMetrics.hospitalised || 0,
      activeColorClass: 'bg-red-600 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'staff-hospitalised',
      filterValue: 'hospitalised'
    },
    {
      label: 'Deceased',
      count: staffMetrics.deceased || 0,
      activeColorClass: 'bg-neutral-900 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'staff-deceased',
      filterValue: 'deceased'
    }
  ];

  // Define site status cards with exact ordering from briefing
  const siteStatusCards = [
    {
      label: 'Active',
      count: siteMetrics.active || 0,
      activeColorClass: 'bg-blue-600 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'sites-active',
      filterValue: 'active'
    },
    {
      label: 'Degraded',
      count: siteMetrics.degraded || 0,
      activeColorClass: 'bg-orange-500 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'sites-degraded',
      filterValue: 'degraded'
    },
    {
      label: 'Evacuating',
      count: siteMetrics.evacuating || 0,
      activeColorClass: 'bg-red-600 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'sites-evacuating',
      filterValue: 'evacuating'
    },
    {
      label: 'Evacuated',
      count: siteMetrics.evacuated || 0,
      activeColorClass: 'bg-neutral-900 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'sites-evacuated',
      filterValue: 'evacuated'
    },
    {
      label: 'Closed and Secure',
      count: siteMetrics.closedAndSecure || 0,
      activeColorClass: 'bg-neutral-500 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'sites-closed',
      filterValue: 'closedAndSecure'
    },
    {
      label: 'Unknown',
      count: siteMetrics.unknown || 0,
      activeColorClass: 'bg-purple-600 text-white',
      inactiveColorClass: 'bg-neutral-100 text-neutral-600',
      testId: 'sites-unknown',
      filterValue: 'unknown'
    }
  ];

  // Calculate totals
  const staffTotal = staffStatusCards.reduce((sum, card) => sum + card.count, 0);
  const siteTotal = siteStatusCards.reduce((sum, card) => sum + card.count, 0);

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* TopCon 4-container layout: Staff Status (35%) | Site Status (35%) | Current Incidents (15%) | Last Update (15%) */}
      <div style={{ 
        display: 'flex', 
        gap: responsive.isMobile ? '12px' : responsive.isTablet ? '14px' : '16px',
        // Responsive layout based on screen size
        flexDirection: responsive.isMobile ? 'column' : 'row',
        flexWrap: responsive.isTablet ? 'wrap' : 'nowrap',
        alignItems: 'stretch', // Ensure all containers have same height
        height: '178px' // Set EXACT height for all containers (reduced by another 10%)
      }}>
        {/* Staff Status Group - 35% width (FIRST) */}
        <div style={{ 
          flex: responsive.isMobile ? '1' : responsive.isTablet ? '1 1 48%' : '3.5', // 35% of total width
          minWidth: responsive.isMobile ? '0' : responsive.isTablet ? '300px' : '250px',
          height: '100%' // Ensure exact height match
        }}>
          <StatusMetricGroup
            title="Staff Status"
            onExport={handleStaffExport}
            exportLoading={staffExportLoading}
            testId="staff-status-group"
            totalCount={staffTotal}
          >
            {staffStatusCards.map((card) => (
              <StatusMetricCard
                key={card.filterValue}
                label={card.label}
                count={card.count}
                activeColorClass={card.activeColorClass}
                inactiveColorClass={card.inactiveColorClass}
                testId={card.testId}
                onClick={(e) => handleBadgeClick(e, card.filterValue, card.filterValue, 'staff')}
              />
            ))}
          </StatusMetricGroup>
        </div>

        {/* Site Status Group - 35% width (SECOND) */}
        <div style={{ 
          flex: responsive.isMobile ? '1' : responsive.isTablet ? '1 1 48%' : '3.5', // 35% of total width
          minWidth: responsive.isMobile ? '0' : responsive.isTablet ? '300px' : '300px',
          height: '100%' // Ensure exact height match
        }}>
          <StatusMetricGroup
            title="Site Status"
            onExport={handleSiteExport}
            exportLoading={siteExportLoading}
            testId="site-status-group"
            totalCount={siteTotal}
          >
            {siteStatusCards.map((card) => (
              <StatusMetricCard
                key={card.filterValue}
                label={card.label}
                count={card.count}
                activeColorClass={card.activeColorClass}
                inactiveColorClass={card.inactiveColorClass}
                testId={card.testId}
                onClick={(e) => handleBadgeClick(e, card.filterValue, card.filterValue, 'site')}
              />
            ))}
          </StatusMetricGroup>
        </div>

        {/* Current Incidents Group - 15% width (THIRD) */}
        <div style={{ 
          flex: responsive.isMobile ? '1' : responsive.isTablet ? '1 1 48%' : '1.5', // 15% of total width
          minWidth: responsive.isMobile ? '0' : responsive.isTablet ? '250px' : '180px',
          height: '100%' // Ensure exact height match
        }}>
          <CurrentIncidentsContainer 
            orgId={orgId} 
            cmtId={activeCmt?.id} 
            includeChildren={true} 
          />
        </div>

        {/* Last Update Group - 15% width (FOURTH) */}
        <div style={{ 
          flex: responsive.isMobile ? '1' : responsive.isTablet ? '1 1 48%' : '1.5', // 15% of total width
          minWidth: responsive.isMobile ? '0' : responsive.isTablet ? '250px' : '180px',
          height: '100%' // Ensure exact height match
        }}>
          <LastUpdateContainer orgId={orgId} />
        </div>
      </div>

      {/* Badge Modal for Staff/Site Details - PRESERVED FUNCTIONALITY */}
      <Modal
        title={
          badgeModalType === 'staff' 
            ? `Staff Status: ${selectedBadgeFilter?.charAt(0)?.toUpperCase()}${selectedBadgeFilter?.slice(1)?.replace('_', ' ')}`
            : `Site Status: ${selectedBadgeFilter?.charAt(0)?.toUpperCase()}${selectedBadgeFilter?.slice(1)}`
        }
        open={showBadgeModal}
        onCancel={() => setShowBadgeModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowBadgeModal(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {badgeModalType === 'staff' && detailedStaffData && !detailedStaffData.loading && (
          <Table
            dataSource={detailedStaffData.staff || []} // FIX: Use .staff array from hook
            columns={[
              { title: 'Name', dataIndex: 'fullName', key: 'fullName', 
                render: (text, record) => record.fullName || record.name || 'Unknown'
              },
              { title: 'Status', dataIndex: 'status', key: 'status', 
                render: (status) => (
                  <Tag color={status === 'safe' ? 'green' : status === 'need_assistance' ? 'orange' : 'red'}>
                    {status?.replace('_', ' ')}
                  </Tag>
                )
              },
              { title: 'Last Updated', dataIndex: 'lastUpdated', key: 'lastUpdated',
                render: (timestamp) => {
                  if (!timestamp) return 'No update';
                  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
                  const dateStr = date.toLocaleDateString('en-AU', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  });
                  const timeStr = date.toLocaleTimeString('en-AU', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: true
                  });
                  return `${dateStr} at ${timeStr}`;
                }
              }
            ]}
            pagination={{ pageSize: 10 }}
            size="small"
            loading={detailedStaffData.loading}
          />
        )}
        
        {badgeModalType === 'site' && detailedSiteData && !detailedSiteData.loading && (
          <Table
            dataSource={detailedSiteData.sites || []} // FIX: Use .sites array from hook
            columns={[
              { title: 'Site Name', dataIndex: 'site_name', key: 'site_name',
                render: (text, record) => record.site_name || record.siteName || 'Unknown'
              },
              { title: 'Status', dataIndex: 'status', key: 'status',
                render: (status) => (
                  <Tag color={status === 'operational' ? 'green' : status === 'alert' ? 'orange' : 'red'}>
                    {status}
                  </Tag>
                )
              },
              { title: 'Location', dataIndex: 'address_line_1', key: 'address_line_1',
                render: (text, record) => {
                  const address1 = record.address_line_1 || '';
                  const address2 = record.address_line_2 || '';
                  const suburb = record.suburb || '';
                  const state = record.state || '';
                  const postcode = record.postcode || '';
                  
                  // Build full address from components
                  const addressParts = [address1, address2, suburb, state, postcode].filter(Boolean);
                  return addressParts.join(', ') || 'No address';
                }
              },
              { title: 'Last Updated', dataIndex: 'last_status_updated_at', key: 'last_status_updated_at',
                render: (timestamp) => {
                  if (!timestamp) return 'No update';
                  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
                  const dateStr = date.toLocaleDateString('en-AU', { 
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  });
                  const timeStr = date.toLocaleTimeString('en-AU', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: true
                  });
                  return `${dateStr} at ${timeStr}`;
                }
              }
            ]}
            pagination={{ pageSize: 10 }}
            size="small"
            loading={detailedSiteData.loading}
          />
        )}
      </Modal>
    </div>
  );
};

export default EnterpriseMetricsBar;